/**
 * Общие константы приложения.
 * Часть экспортов (ERROR_CODES, HTTP_STATUS, USER_ROLES, ROLE_CONSTANTS и др.) зарезервирована для единообразия и будущего использования.
 */

/**
 * Размеры файлов в байтах
 */
/**
 * Slug организации GoodLuck. Некоторые фичи включены пока только для неё
 * (напр. анимированные GIF-баннеры турниров). Slug стабилен между окружениями,
 * в отличие от id организации.
 */
export const GOODLUCK_ORG_SLUG = "goodluck";

export const FILE_SIZES = {
  /** Максимальный размер аватара: 5MB (GIF Pro и тяжёлые PNG) */
  AVATAR_MAX_SIZE: 5 * 1024 * 1024,
  /** Максимальный размер баннера: 5MB */
  BANNER_MAX_SIZE: 5 * 1024 * 1024,
  /** Максимальный размер видео-баннера (mp4 → анимированный webp, только goodluck): 26MB */
  VIDEO_BANNER_MAX_SIZE: 26 * 1024 * 1024,
  /** Максимальный размер команды: 2MB */
  TEAM_MAX_SIZE: 2 * 1024 * 1024,
  /** Максимальный размер турнира: 5MB */
  TOURNAMENT_MAX_SIZE: 5 * 1024 * 1024,
  /** Максимальный размер организации: 5MB */
  ORGANIZATION_MAX_SIZE: 5 * 1024 * 1024,
  /** Максимальный размер достижения: 2MB */
  ACHIEVEMENT_MAX_SIZE: 2 * 1024 * 1024,
  /** Максимальный размер гайда: 5MB */
  GUIDE_MAX_SIZE: 5 * 1024 * 1024,
  /** Максимальный размер правил турнира (PDF/DOC/TXT): 10MB */
  RULEBOOK_MAX_SIZE: 10 * 1024 * 1024,
  /** Максимальный размер вложений чата (голос, картинка, видео): 50MB — сервер принимает столько же */
  CHAT_MAX_SIZE: 50 * 1024 * 1024,
  /** Максимальный размер по умолчанию: 5MB */
  DEFAULT_MAX_SIZE: 5 * 1024 * 1024,
} as const;

/**
 * Лимиты валидации
 */
export const VALIDATION_LIMITS = {
  /** Минимальная длина названия турнира */
  TOURNAMENT_NAME_MIN: 3,
  /** Максимальная длина названия турнира */
  TOURNAMENT_NAME_MAX: 100,
  /** Максимальная длина описания */
  DESCRIPTION_MAX: 1000,
  /** Минимальное количество команд */
  MIN_TEAMS: 2,
  /** Максимальное количество команд */
  MAX_TEAMS: 128,
  /** Минимальная длина имени пользователя */
  USERNAME_MIN: 3,
  /** Максимальная длина имени пользователя */
  USERNAME_MAX: 25,
  /** Максимальная длина описания профиля */
  PROFILE_ABOUT_MAX: 500,
  /** Максимальная длина локации */
  LOCATION_MAX: 20,
  /** Максимальная длина веб-сайта */
  WEBSITE_MAX: 50,
  /** Максимальная длина описания организации */
  ORGANIZATION_DESCRIPTION_MAX: 500,
  /** Максимальная длина описания команды */
  TEAM_DESCRIPTION_MAX: 300,
  /** Максимальная длина email */
  EMAIL_MAX: 100,
  /** Максимальная длина пароля */
  PASSWORD_MAX: 100,
  /** Минимальная длина пароля */
  PASSWORD_MIN: 6,
  /** Максимальная длина отображаемого имени */
  DISPLAY_NAME_MAX: 100,
  /** Максимальная длина названия команды */
  TEAM_NAME_MAX: 30,
  /** Максимальная длина тега команды */
  TEAM_TAG_MAX: 10,
  /** Минимальная длина тега команды */
  TEAM_TAG_MIN: 2,
  /** Максимальное количество участников команды */
  TEAM_MAX_MEMBERS: 4,
  /** Максимальное количество языков */
  MAX_LANGUAGES: 10,
  /** Максимальная длина социальной ссылки */
  SOCIAL_LINK_MAX: 100,
  /** Максимальная длина никнейма Riot */
  RIOT_NICKNAME_MAX: 16,
  /** Максимальная длина тега Riot */
  RIOT_TAG_MAX: 5,
  /** Максимальная длина кода подтверждения */
  VERIFICATION_CODE_MAX: 6,
} as const;

/**
 * Системные константы для интеграций
 */
export const SYSTEM_CONSTANTS = {
  /** Discord интеграция */
  DISCORD: {
    CLIENT_ID: "1160542557995118602",
    ADMIN_PERMISSIONS: "8",
    ADMIN_CLIENT_ID: "1275007419981893694",
  },
  /** Twitch интеграция */
  TWITCH: {
    THUMBNAIL_WIDTH: "1280",
    THUMBNAIL_HEIGHT: "720",
  },
  /** Коды подтверждения */
  VERIFICATION: {
    PLACEHOLDER: "000000",
  },
} as const;

/**
 * Математические константы
 */
export const MATH_CONSTANTS = {
  /** Для генерации случайных иконок */
  RANDOM_ICON_MAX: 29,
  /** Для генерации ID команд */
  TEAM_ID_LENGTH: 9,
} as const;

/**
 * Форматирование чисел
 */
export const NUMBER_FORMATTING = {
  /** Порог для отображения в тысячах */
  THOUSAND_THRESHOLD: 1000,
  /** Порог для отображения в миллионах */
  MILLION_THRESHOLD: 1000000,
  /** Количество знаков после запятой для K/M */
  DECIMAL_PLACES: 1,
} as const;

/**
 * Временные интервалы в миллисекундах
 */
export const TIME_INTERVALS = {
  /** 1 секунда */
  SECOND: 1000,
  /** 5 секунд */
  FIVE_SECONDS: 5 * 1000,
  /** 10 секунд */
  TEN_SECONDS: 10 * 1000,
  /** 30 секунд */
  THIRTY_SECONDS: 30 * 1000,
  /** 1 минута */
  MINUTE: 60 * 1000,
  /** 5 минут */
  FIVE_MINUTES: 5 * 60 * 1000,
  /** 10 минут */
  TEN_MINUTES: 10 * 60 * 1000,
  /** 30 минут */
  THIRTY_MINUTES: 30 * 60 * 1000,
  /** 1 час */
  HOUR: 60 * 60 * 1000,
  /** 1 день */
  DAY: 24 * 60 * 60 * 1000,
  /** 7 дней */
  WEEK: 7 * 24 * 60 * 60 * 1000,
  /** 30 дней */
  MONTH: 30 * 24 * 60 * 60 * 1000,
} as const;

/**
 * Анимации и переходы
 */
export const ANIMATION_DURATIONS = {
  /** Быстрая анимация */
  FAST: 200,
  /** Обычная анимация */
  NORMAL: 300,
  /** Медленная анимация */
  SLOW: 500,
  /** Очень медленная анимация */
  VERY_SLOW: 1000,
  /** Мигание курсора */
  CARET_BLINK: 1000,
} as const;

/**
 * Валидирует только критически важные константы
 */
function validateCriticalConstants(): void {
  // Валидация размеров файлов (критично для безопасности)
  Object.entries(FILE_SIZES).forEach(([key, value]) => {
    if (value <= 0) {
      throw new Error(
        `FILE_SIZES.${key} должен быть положительным числом, получено: ${value}`,
      );
    }
  });

  // Валидация лимитов команд (критично для бизнес-логики)
  if (VALIDATION_LIMITS.MIN_TEAMS < 2) {
    throw new Error("MIN_TEAMS должен быть не менее 2");
  }
  if (VALIDATION_LIMITS.MAX_TEAMS > 1000) {
    throw new Error("MAX_TEAMS не должен превышать 1000");
  }

  // Валидация только критически важных системных констант
  if (
    !SYSTEM_CONSTANTS.DISCORD.CLIENT_ID ||
    SYSTEM_CONSTANTS.DISCORD.CLIENT_ID.length < 10
  ) {
    throw new Error("DISCORD.CLIENT_ID должен быть валидным ID");
  }
}

// Запускаем валидацию только критических констант
validateCriticalConstants();
